import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ArrowLeft, GitCompare } from "lucide-react";
import api from "../api";
import { useAppContext } from "../context/AppContext";
import LoadingSpinner from "../components/LoadingSpinner";
import PCard from "../components/PCard";

export default function FavoritesPage() {
  const { lang, cur, user } = useAppContext();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    api.get("/api/favorites")
      .then(data => setFavorites(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const t = {
    en: { title: "My Favorites", sub: "Your saved properties", browse: "Browse Properties", empty: "No saved properties yet", emptySub: "Heart a property on any listing to save it here.", compare: "Go to Comparison" },
    ar: { title: "المفضلة", sub: "عقاراتك المحفوظة", browse: "تصفح العقارات", empty: "لا توجد عقارات محفوظة بعد", emptySub: "احفظ عقارًا من القائمة لعرضه هنا.", compare: "انتقل للمقارنة" },
  }[lang];

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "var(--cream)", paddingTop: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <LoadingSpinner size={36} trackColor="rgba(201,168,76,.2)" />
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "var(--cream)", paddingTop: 80 }}>
      {/* Page header */}
      <div style={{ background: "linear-gradient(135deg,var(--navy),var(--navy2))", padding: "3.5rem 5% 3rem" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: ".6rem", marginBottom: ".6rem" }}>
                <Heart size={18} fill="var(--gold)" stroke="var(--gold)" />
                <span style={{ fontSize: ".6rem", color: "var(--gold)", letterSpacing: ".22em", textTransform: "uppercase", fontFamily: "var(--sans)" }}>
                  {lang === "en" ? "Saved Properties" : "العقارات المحفوظة"}
                </span>
              </div>
              <h1 style={{ fontFamily: "var(--serif)", fontSize: "clamp(1.8rem,4vw,2.6rem)", color: "#fff", fontWeight: 300, lineHeight: 1.1, marginBottom: ".4rem" }}>{t.title}</h1>
              <p style={{ color: "rgba(255,255,255,.4)", fontSize: ".9rem", fontFamily: "var(--sans)" }}>
                {t.sub} · {favorites.length} {lang === "en" ? (favorites.length === 1 ? "property" : "properties") : "عقار"}
              </p>
            </div>
            <div style={{ display: "flex", gap: ".7rem", flexWrap: "wrap" }}>
              <button
                className="btn-o"
                onClick={() => navigate("/comparison")}
                style={{ borderRadius: 6, padding: "9px 18px", fontSize: ".8rem", display: "flex", alignItems: "center", gap: 6 }}
              >
                <GitCompare size={14} />{t.compare}
              </button>
              <button
                className="btn-g"
                onClick={() => navigate("/properties")}
                style={{ borderRadius: 6, padding: "9px 18px", fontSize: ".8rem", display: "flex", alignItems: "center", gap: 6 }}
              >
                <ArrowLeft size={14} />{t.browse}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "3rem 5% 5rem" }}>
        {favorites.length === 0 ? (
          <div style={{ textAlign: "center", padding: "6rem 1rem" }}>
            <div style={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(201,168,76,.1)", border: "1px solid rgba(201,168,76,.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.5rem" }}>
              <Heart size={36} stroke="var(--gold)" strokeWidth={1.25} />
            </div>
            <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.7rem", color: "var(--navy)", fontWeight: 300, marginBottom: ".5rem" }}>{t.empty}</h2>
            <p style={{ color: "var(--gray)", fontSize: ".9rem", marginBottom: "2rem" }}>{t.emptySub}</p>
            <button className="btn-g" onClick={() => navigate("/properties")} style={{ borderRadius: 6, padding: "11px 28px", fontSize: ".86rem" }}>
              {t.browse}
            </button>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(min(320px,100%),1fr))", gap: "1.5rem" }}>
            {favorites.map(fav => (
              <PCard key={fav.id} p={fav.project} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
